import { LexoRank } from 'lexorank'
import { logger } from '@/lib/monitoring/logger'

/**
 * Generate an initial rank for a new item (at the middle of the list)
 * @returns A string representation of the initial rank
 */
export function generateInitialRank(): string {
  return LexoRank.middle().toString()
}

/**
 * Generate a rank between two existing ranks
 * @param before The rank of the item before the new position (or null if at the beginning)
 * @param after The rank of the item after the new position (or null if at the end)
 * @returns A string representation of the new rank
 */
/**
 * Validate if a string is a valid LexoRank
 */
function isValidLexoRank(rank: string | null | undefined): rank is string {
  if (!rank || rank === '' || typeof rank !== 'string') {
    return false
  }
  // LexoRank format: bucket|value (e.g., "0|hzzzzz:")
  // Must contain a pipe and have valid format
  if (!rank.includes('|')) {
    return false
  }
  return true
}

export function generateRankBetween(
  before: string | null | undefined,
  after: string | null | undefined,
): string {
  try {
    // Validate inputs
    const validBefore = isValidLexoRank(before)
    const validAfter = isValidLexoRank(after)

    // Handle invalid/missing cases
    if (!validBefore && !validAfter) {
      return generateInitialRank()
    }

    if (!validBefore) {
      // Insert at the beginning - after must be valid
      return LexoRank.parse(after!).genPrev().toString()
    }

    if (!validAfter) {
      // Insert at the end - before must be valid
      return LexoRank.parse(before).genNext().toString()
    }

    // Insert between two ranks
    const beforeRank = LexoRank.parse(before)
    const afterRank = LexoRank.parse(after)
    return beforeRank.between(afterRank).toString()
  } catch (error) {
    logger.logDebug('LexoRank parsing failed, using default', {
      before,
      after,
      error: String(error),
    })
    // Fallback to generating a new middle rank
    return generateInitialRank()
  }
}

/**
 * Generate the next rank after an existing rank
 * @param rank The existing rank
 * @returns A string representation of the next rank
 */
export function generateNextRank(rank: string | null | undefined): string {
  if (!isValidLexoRank(rank)) {
    return generateInitialRank()
  }
  try {
    return LexoRank.parse(rank).genNext().toString()
  } catch (error) {
    logger.logDebug('LexoRank parsing failed in generateNextRank, using default', {
      rank,
      error: String(error),
    })
    return generateInitialRank()
  }
}

/**
 * Generate the previous rank before an existing rank
 * @param rank The existing rank
 * @returns A string representation of the previous rank
 */
export function generatePrevRank(rank: string | null | undefined): string {
  if (!isValidLexoRank(rank)) {
    return generateInitialRank()
  }
  try {
    return LexoRank.parse(rank).genPrev().toString()
  } catch (error) {
    logger.logDebug('LexoRank parsing failed in generatePrevRank, using default', {
      rank,
      error: String(error),
    })
    return generateInitialRank()
  }
}

/**
 * Compare two ranks to determine their order
 * @param a The first rank
 * @param b The second rank
 * @returns A negative number if a < b, a positive number if a > b, or 0 if a = b
 */
export function compareRanks(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  // Handle null/undefined values
  if (!a && !b) return 0
  if (!a) return -1
  if (!b) return 1

  // Parse and compare the ranks. Fall back to lexicographic compare if either
  // side is malformed (e.g. legacy seed data) so a single bad row can't crash
  // the entire list render.
  try {
    const rankA = LexoRank.parse(a)
    const rankB = LexoRank.parse(b)
    return rankA.compareTo(rankB)
  } catch (error) {
    logger.logDebug('LexoRank compareRanks failed, falling back to string compare', {
      a,
      b,
      error: String(error),
    })
    if (a === b) return 0
    return a < b ? -1 : 1
  }
}
